/**
 * ShiptimizeClassicCheckout — PUDO flow for the WooCommerce classic (shortcode) checkout.
 *
 * Responsibilities:
 *  - Extract the shipping address from classic checkout form fields
 *  - Detect which shipping method is selected and its carrier ID
 *  - Read pickup behaviour (optional / mandatory) from PHP data attributes
 *  - Update ShiptimizeState when any of the above changes
 *  - React to state changes to update the UI (button, toast, pickup button)
 *
 * This module does NOT write to form fields or localStorage directly.
 * All persistent state flows through ShiptimizeState.
 */
import { PICKUP_BEHAVIOUR } from '../state/shiptimize-state.js';

export default class ShiptimizeClassicCheckout {
  /**
   * @param {ShiptimizeState} state
   * @param {ShiptimizeUI}    ui
   * @param {function}        onOpenMap  — called when the pickup button is clicked and ready
   */
  constructor(state, ui, onOpenMap) {
    this._state               = state;
    this._ui                  = ui;
    this._onOpenMap           = onOpenMap;
    this._lastValidatedRateId = null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  init() {
    // Poll until the classic checkout form is ready in the DOM
    const poll = setInterval(() => {
      if (jQuery('form.checkout, .woocommerce-checkout').length) {
        clearInterval(poll);
        this._setupHandlers();
      }
    }, 500);
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  _setupHandlers() {
    this._extractAddress();
    this.evaluate();

    // Address or shipping method field changed — broad selector catches
    // standard WC, Elementor widget, and Avada builder wrapping the form
    jQuery(document).on('change', 'form.checkout, form.woocommerce-checkout', () => {
      this._extractAddress();
      this.evaluate();
    });

    // WooCommerce re-renders shipping rates after address change
    jQuery(document.body).on('updated_checkout', () => {
      this._extractAddress();
      this.evaluate();
    });

    // WooCommerce AJAX fragment refresh — fired in some WC versions/themes
    // instead of or alongside updated_checkout
    jQuery(document.body).on('woocommerce_checkout_fragments_refreshed', () => {
      this._extractAddress();
      this.evaluate();
    });

    // Elementor re-renders checkout widget after navigation / popup
    jQuery(document.body).on('elementor/frontend/init elementor/popup/show', () => {
      setTimeout(() => { this._extractAddress(); this.evaluate(); }, 400);
    });

    // Avada / Fusion Builder content change
    jQuery(document.body).on('fusion-content-changed', () => {
      setTimeout(() => { this._extractAddress(); this.evaluate(); }, 400);
    });

    // Common multi-step checkout plugins that reveal the shipping step
    // without triggering WC's standard updated_checkout event
    jQuery(document.body).on(
      'woomc_step_loaded fc_checkout_fragments_load_complete fc_checkout_fragment_load_complete',
      () => { setTimeout(() => { this._extractAddress(); this.evaluate(); }, 300); }
    );

    // Shipping method radio button changed (native DOM — catches dynamically added radios)
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name*="shipping_method"]')) {
        this._state.clearPickup();
        this._ui.clearPickupDisplay();
        this.evaluate();
      }
    });

    // MutationObserver — catches Elementor/Avada/WPBakery async DOM rebuilds
    // that don't fire jQuery events (e.g. theme re-rendering shipping rate list)
    this._setupMutationObserver();

    // IntersectionObserver — catches multi-step checkout themes that reveal the
    // shipping section via CSS (display:none → block) rather than DOM insertion.
    // MutationObserver misses visibility changes; IntersectionObserver catches them.
    this._setupVisibilityObserver();

    // Self-terminating retry — WooCommerce often calculates shipping rates via AJAX
    // AFTER the form is visible. evaluate() returns early if no radio is checked yet.
    // This retries up to 8 × 500ms = 4 seconds, stopping as soon as a carrier is found.
    this._startInitialRetry();
  }

  _setupMutationObserver() {
    // Observe from the checkout form outward; fall back to body if the form
    // isn't in the DOM yet (Elementor lazy-renders it)
    const root = document.querySelector(
      'form.checkout, form.woocommerce-checkout, .elementor-widget-woocommerce-checkout'
    ) || document.body;

    let debounce = null;

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) =>
        [...m.addedNodes, ...m.removedNodes].some((n) => {
          if (n.nodeType !== 1) return false;
          return (
            n.matches?.('input[name*="shipping_method"], .woocommerce-shipping-methods') ||
            n.querySelector?.('input[name*="shipping_method"]') !== null
          );
        })
      );
      if (!relevant) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => { this._extractAddress(); this.evaluate(); }, 300);
    });

    observer.observe(root, { childList: true, subtree: true });
    this._observer = observer; // store ref so it can be disconnected if needed
  }

  /**
   * Watch for the shipping methods container to enter the viewport.
   *
   * Multi-step checkout themes (OceanWP, Divi, Flatsome, Astra Pro, etc.) hide
   * the shipping section with display:none and reveal it with a JS class toggle.
   * No DOM nodes are added, so MutationObserver stays silent. IntersectionObserver
   * fires as soon as the element becomes visible regardless of how that happened.
   *
   * Disconnects itself once a carrier ID is found to avoid redundant re-evaluations
   * on normal user scrolling.
   */
  _setupVisibilityObserver() {
    if (!window.IntersectionObserver) return;

    let attempts = 0;

    const observeWhenReady = () => {
      attempts++;
      const target = document.querySelector(
        '.woocommerce-shipping-methods, #shipping_method, .woocommerce-shipping-totals'
      );

      if (target) {
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this._extractAddress();
          this.evaluate();
          if (this._state.get('carrierId')) {
            io.disconnect();
            this._intersectionObserver = null;
          }
        }, { threshold: 0.05 });

        io.observe(target);
        this._intersectionObserver = io;
      } else if (attempts < 6) {
        // Retry up to ~5 s in case the shipping list is added asynchronously
        setTimeout(observeWhenReady, 800);
      }
    };

    observeWhenReady();
  }

  /**
   * Retry evaluate() up to 8 times × 500ms after _setupHandlers() runs.
   *
   * WooCommerce calculates shipping rates via its own AJAX call after the checkout
   * form is rendered. On the very first evaluate() the rate list may not be ready
   * yet — no radio is checked, so evaluate() returns early and the pickup button
   * never appears. updated_checkout fires when WC finishes, but some themes or
   * caching plugins suppress it. This retry fills that gap with no risk: evaluate()
   * is idempotent and the interval clears itself the moment a carrier is detected.
   */
  _startInitialRetry() {
    if (this._state.get('carrierId')) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts++;
      if (this._state.get('carrierId') || attempts >= 8) {
        clearInterval(timer);
        return;
      }
      this._extractAddress();
      this.evaluate();
    }, 500);
  }

  /**
   * Read address fields from the classic checkout form and push into state.
   * State.set('address') will automatically clear the pickup point if the
   * address has meaningfully changed.
   */
  _extractAddress() {
    const useShipping = jQuery('#ship-to-different-address input').is(':checked');
    const prefix      = useShipping ? 'shipping' : 'billing';

    this._state.set('address', {
      Streetname1: jQuery(`#${prefix}_address_1`).val() || '',
      Streetname2: jQuery(`#${prefix}_address_2`).val() || '',
      City:        jQuery(`#${prefix}_city`).val()      || '',
      PostalCode:  jQuery(`#${prefix}_postcode`).val()  || '',
      State:       jQuery(`#${prefix}_state`).val()     || '',
      Country:     jQuery(`#${prefix}_country`).val()   || '',
      Lat:         '',
      Long:        '',
    });
  }

  /**
   * Read the currently selected shipping method from the DOM.
   * Determine carrier ID and pickup behaviour from the method value and
   * PHP-rendered data attributes, then update state and UI accordingly.
   *
   * Called on every form change and checkout update.
   */
  evaluate() {
    const selectedInput = jQuery('input[name^="shipping_method"]:checked');
    if (!selectedInput.length) return;

    const methodValue = selectedInput.val();
    if (!methodValue) return;

    // Generic pattern — matches any brand: shiptimize_30, CTT_30, GLSDk_30, bpost_68, etc.
    // Consistent with ShiptimizeBlockCheckout._extractCarrierIdFromMethodId()
    const match     = methodValue.match(/shipping_[A-Za-z][A-Za-z0-9]*_(\d+)/i);
    const carrierId = match ? parseInt(match[1]) : null;

    // Clear pickup whenever the user switches to a different method, regardless
    // of whether the carrier ID is the same (e.g. CTT Flat Rate vs CTT WBS
    // both have carrier 30 but must have independent pickup state).
    const rateChanged =
      this._lastValidatedRateId !== null &&
      methodValue !== this._lastValidatedRateId;

    if (rateChanged) {
      this._state.clearPickup();
      this._ui.clearPickupDisplay();
      this._ui.dismissToast();
      this._ui.resetMandatoryToast();
    }

    if (!carrierId) {
      this._ui.removeAllPickupButtons();
      this._ui.enableButton();
      this._state.clearCarrier();
      jQuery('#shipping_carrier_id').remove();
      return;
    }

    // Update state only when carrier actually changed
    if (carrierId !== this._state.get('carrierId')) {
      this._state.set('carrierId', carrierId);
    }

    // Keep the hidden form field in sync so PHP receives it on order submit
    // (read via filter_input(INPUT_POST, 'shipping_carrier_id') in shiptimize_order_submited)
    if (jQuery('#shipping_carrier_id').length === 0) {
      jQuery('form.checkout, form.woocommerce-checkout').first().append(
        `<input type="hidden" id="shipping_carrier_id" name="shipping_carrier_id" value="${carrierId}">`
      );
    } else {
      jQuery('#shipping_carrier_id').val(carrierId);
    }

    // Read pickup capability from PHP-rendered data attributes
    const labelOrLi      = selectedInput.closest('label, li');
    const hasPickup      = labelOrLi.find('[data-has-pickup="1"]').length > 0
                        || selectedInput.attr('data-has-pickup') === '1';
    const isMandatory    = labelOrLi.find('[data-pickup-mandatory="1"]').length > 0
                        || selectedInput.attr('data-pickup-mandatory') === '1';

    if (!hasPickup) {
      this._ui.removeAllPickupButtons();
      this._ui.clearPickupDisplay();
      this._ui.dismissToast();
      this._ui.enableButton();
      this._state.clearCarrier();
      return;
    }

    const behaviour = isMandatory ? PICKUP_BEHAVIOUR.MANDATORY : PICKUP_BEHAVIOUR.OPTIONAL;
    this._state.set('pickupBehaviour', behaviour);

    this._renderPickupButton();

    // Re-sync state into the (potentially freshly re-rendered) hidden fields.
    // WC AJAX fragment refresh wipes our injected fields; without this the
    // pickup point in state would never reach the form on submit.
    this._state._syncToFormFields();

    const pickupPoint     = this._state.get('pickupPoint');
    const storedCarrierId = parseInt(localStorage.getItem('ShiptimizePickupCarrierId') || '0');
    const pickupIsValid   = pickupPoint && storedCarrierId === carrierId;

    // Always clear ALL pickup labels first (prevents bleeding across methods),
    // then restore only for the currently selected method.
    this._ui.clearPickupDisplay();
    if (pickupIsValid) {
      this._ui.showPickupLabel(pickupPoint.label);
    }

    if (behaviour === PICKUP_BEHAVIOUR.MANDATORY && !pickupIsValid) {
      if (pickupPoint) {
        // Stale pickup from a different carrier — clear it
        this._state.clearPickup();
        this._ui.clearPickupDisplay();
      }
      this._ui.disableButton();
      this._ui.showMandatoryToastOnce(carrierId);
    } else {
      this._ui.enableButton();
      jQuery('#pickup-required-notice').remove();
    }

    this._lastValidatedRateId = methodValue;
  }

  /**
   * Inject the "Choose Pickup Location" button into the checkout.
   * Idempotent — does nothing if the button is already there.
   *
   * Strategy:
   *  1. Use the PHP-rendered .shiptimize-shipping-options container (standard WC template).
   *  2. If missing (custom template, Elementor, Avada, WPBakery etc.) — create the container
   *     and insert it near the shipping method list so the button always appears.
   */
  _renderPickupButton() {
    let container = jQuery('.GLSDk-shipping-options, .shiptimize-shipping-options');

    if (!container.length) {
      // PHP container wasn't rendered (non-standard template).
      // Create our own and insert it after the shipping methods list.
      const insertionSelectors = [
        '.woocommerce-shipping-methods',     // standard WC shipping list
        '#shipping_method',                  // fallback: WC shipping method wrapper
        '.woocommerce-checkout-review-order',
        '#order_review',
        'form.checkout',
        'form.woocommerce-checkout',
      ];
      let inserted = false;
      for (const sel of insertionSelectors) {
        const target = jQuery(sel).first();
        if (target.length) {
          target.after('<table class="shiptimize-shipping-options"></table>');
          inserted = true;
          break;
        }
      }
      if (!inserted) return; // nothing in the DOM we can attach to — bail
      container = jQuery('.shiptimize-shipping-options');
    }

    if (container.find('.shiptimize-pick-location').length) return; // already injected

    container.html(`
      <tr><td>
        <button class="button alt shiptimize-pick-location" type="button">
          ${window.shiptimize_choose_pickup_location || 'Choose Pickup Location'}
        </button>
        <input type="hidden" name="shipping_pickup_id"         id="shipping_pickup_id"/>
        <input type="hidden" name="shipping_pickup_label"      id="shipping_pickup_label"/>
        <input type="hidden" name="shiptimize_pickup_extended" id="shipping_pickup_extended"/>
        <span class="shiptimize-pickup__description"></span>
      </td></tr>
    `).show();

    container.find('.shiptimize-pick-location').on('click', (e) => {
      e.preventDefault();
      this._handlePickupButtonClick();
    });
  }

  _handlePickupButtonClick() {
    const address  = this._state.get('address');
    const required = ['Streetname1', 'City', 'PostalCode', 'Country'];

    if (!required.every((f) => address?.[f]?.trim())) {
      this._ui.showAddressRequiredModal();
      return;
    }

    if (!this._state.get('carrierId')) {
      this._ui.showToast('<strong>Error:</strong> Could not determine shipping carrier. Please refresh the page.', 5000);
      return;
    }

    this._onOpenMap();
  }

  // Show the mandatory pickup toast only once per carrier to avoid spamming
}
